import { useCallback } from "react";
import { useAuth, usePermissionKeys } from "../contexts/AuthContext";
import { hasPermission as sharedHasPermission } from "@legal-500/auth-client";

/**
 * Hook to check user permissions
 *
 * Permissions available:
 * - taxonomy.read: Read taxonomy data
 * - taxonomy.write: Create and update taxonomy data
 * - taxonomy.delete: Delete taxonomy data
 * - taxonomy.bulk_import: Import bulk data
 * - fulfilment.read_subscriptions: Read subscriptions
 * - fulfilment.read_packages: Read packages
 * - fulfilment.read_features: Read features
 * - fulfilment.create_subscription: Create subscriptions
 * - fulfilment.edit_subscription: Edit and cancel subscriptions
 * - fulfilment.create_package: Create, update, and delete packages
 * - fulfilment.id_matching: Update subscription metadata for ID matching
 * - user.read_accounts: Read user accounts
 *
 * @example
 * const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
 *
 * if (hasPermission('taxonomy.write')) {
 *   // Show create button
 * }
 */
export function usePermissions() {
  const { user } = useAuth();
  const permissions = usePermissionKeys();

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return sharedHasPermission(permission, permissions);
    },
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (perms: string[]): boolean => {
      return permissions.includes("*") || perms.some(p => permissions.includes(p));
    },
    [permissions],
  );

  const hasAllPermissions = useCallback(
    (perms: string[]): boolean => {
      return permissions.includes("*") || perms.every(p => permissions.includes(p));
    },
    [permissions],
  );

  const hasRole = useCallback(
    (roleKey: string): boolean => {
      return user?.roles?.some((r: { key: string }) => r.key === roleKey) || false;
    },
    [user],
  );

  const hasAnyRole = useCallback(
    (roleKeys: string[]): boolean => {
      return roleKeys.some(key => hasRole(key));
    },
    [hasRole],
  );

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    permissions,
    roles: user?.roles || [],
    user,
  };
}
